import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/features/toast";
import axios from "axios";

interface DevLoginProps {
  onLogin: () => void;
}

// Local storage key
const AUTH_KEY = "harmony_dev_auth";

// API instance
const api = axios.create({
  baseURL: "https://api.harmonyai.net",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: true,
});

// Interceptor ekleyelim
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(AUTH_KEY);
    }
    return Promise.reject(error);
  }
);

export const DevLogin: React.FC<DevLoginProps> = ({ onLogin }) => {
  const { t } = useTranslation();
  const { success, error } = useToast();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // İlk yüklemede auth kontrolü
  useEffect(() => {
    const savedAuth = localStorage.getItem(AUTH_KEY);
    if (savedAuth === "authenticated") {
      onLogin();
    }
  }, [onLogin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await api.post("/admin/login", {
        username,
        password,
      });

      if (response.status === 200) {
        // Başarılı giriş
        localStorage.setItem(AUTH_KEY, "authenticated");
        success(t("login.welcomeDev"), {
          title: t("login.success"),
        });
        onLogin();
      }
    } catch (errorCatch) {
      error(
        axios.isAxiosError(errorCatch) && errorCatch.response?.data?.message
          ? errorCatch.response.data.message
          : t("login.invalidCredentials"),
        {
          title: t("login.error"),
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md p-6 mx-auto rounded-lg border shadow-lg bg-card"
      >
        <div className="flex flex-col items-center justify-center space-y-2 text-center mb-6">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", damping: 10, stiffness: 100 }}
            className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-2"
          >
            <LockIcon className="h-8 w-8 text-primary" />
          </motion.div>
          <h2 className="text-2xl font-bold">{t("login.devEnvironment")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("login.restrictedAccess")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="username" className="text-sm font-medium">
              {t("login.username")}
            </label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t("login.enterUsername")}
              required
              autoComplete="off"
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium">
              {t("login.password")}
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("login.enterPassword")}
              required
              autoComplete="off"
              className="w-full"
            />
          </div>

          <Button type="submit" className="w-full mt-4" disabled={isLoading}>
            {isLoading ? (
              <span className="flex items-center">
                <LoadingSpinner className="mr-2 h-4 w-4 animate-spin" />
                {t("login.authenticating")}
              </span>
            ) : (
              t("login.login")
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            {t("login.disclaimer")}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// Loading spinner component
const LoadingSpinner = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

// Lock Icon
const LockIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
